import React, { useEffect, useState } from "react";
import cx from "classnames";
import { encode, decode } from "shopify-gid";
import { RevealOnMount } from "src/components/reveal-on-mount";
import { SEO } from "src/components/SEO";
import { ProductHero } from "src/components/product/hero";
import { ProductSchema } from "src/components/product/schema";
import { ProductContentModule } from "src/components/product/content-module";
import { ProductForm } from "src/components/product/form";
import { client, useSetPage } from "src/context/siteContext";
import useRequireAuth from '../hooks/useRequireAuth';

export interface ProductProps {
  location: any;
  pageContext: {
    main: {
      slug: {
        current: string;
      };
      title: string;
      mainImage: any;
      hoverImage?: any;
      productDetails: [
        {
          _type: string;
          children: any;
        }
      ];
      modules: [
        {
          _key: string;
        }
      ];
      productCourse?: {
        isCourse?: boolean;
        _type?: string;
        courseInstructor?: string;
        courseDescription?: {
          description: [any];
        };
        courseCover?: {
          asset: any;
        };
        courseContent?: [any]
      }
    };
    meta: {};
    shopify: any;
  };
}

const Product = ({ location, pageContext }: ProductProps) => {
  const [ isAuthenticated, customer] = useRequireAuth()
  const { main } = pageContext;
  const {
    main: { modules, slug, productCourse, isPreOrder },
    shopify,
  } = pageContext;

  const setPage = useSetPage();

  const [isMounted, setIsMounted] = useState(false);
  const [activeVariantId, setActiveVariantId] = useState(
    shopify.defaultVariant.variantId
  );

  const [available, setAvailable] = useState(false);
  const [liveVariants, setLiveVariants] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [activeLiveVariant, setActiveLiveVariant] = useState({});
  const [activeEncodedVariantId, setActiveEncodedVariantId] = useState(
    "" as string
  );
  const [compareAtPrice, setCompareAtPrice] = useState(
    undefined as string | undefined
  );
  const [check, setCheck] = useState(true);

  useEffect(() => {
    if (check) {
      const shopifyId = encode("Product", shopify.productId);
      client.product.fetch(shopifyId).then((product: any) => {
        const variants = [] as any;
        product.variants.forEach((variant: any) => {
          variants.push({
            ...variant,
            cleanId: parseInt(variant.id.split("/ProductVariant/")[1], 0),
            // cleanId: parseInt(decode(variant.id).id, 0),
            disabled: !variant.available,
            value: variant.id,
          });
        });
        setLiveVariants(variants);

        const options = product.options.map(({ id, name, values }) => ({
          id,
          name,
          values: values.map(({ value }) => value),
        }));
        setProductOptions(options);

        const firstAvailableLiveVariant =
          variants.find((variant: any) => variant.available) || variants[0];

        setActiveLiveVariant(firstAvailableLiveVariant);
        setAvailable(firstAvailableLiveVariant.available);

        if (firstAvailableLiveVariant.compareAtPrice) {
          setCompareAtPrice(firstAvailableLiveVariant.compareAtPrice);
        }

        // const encodedVariantId = encode("ProductVariant", firstAvailableLiveVariant.id);
        const encodedVariantId = firstAvailableLiveVariant.id;

        setActiveVariantId(firstAvailableLiveVariant.cleanId);
        setActiveEncodedVariantId(encodedVariantId);

        setCheck(false);
      });
    }
  }, [check]);

  const handleVariantSelect = (
    e: React.FormEvent,
    variantId: number,
    variantAvailable: boolean
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setActiveVariantId(variantId);

    const encodedVariantId = encode("ProductVariant", variantId);
    setActiveEncodedVariantId(encodedVariantId);

    const activeLiveVariant = liveVariants.find(
      (variant) => variant.cleanId === variantId
    );
    setActiveLiveVariant(activeLiveVariant);
    setAvailable(variantAvailable);
  };

  const productDetails =
    main.productDetails &&
    main.productDetails
      // loop through each block
      .map((block) => {
        // if it's not a text block with children,
        // return nothing
        if (block._type !== "block" || !block.children) {
          return "";
        }
        // loop through the children spans, and join the
        // text strings
        return block.children.map((child) => child.text).join("");
      })
      // join the paragraphs leaving split by two linebreaks
      .join("\n\n");

  // Let's just set meta tags from content
  const meta = {
    metaTitle: main.title,
    metaDescription: productDetails && productDetails,
    openImage: main.mainImage,
    twitterImage: main.mainImage,
  };

  // Setup the analytics product object
  const productAnalyticsObject = {
    id: shopify.defaultVariant.variantId,
    title: main.title,
    category: shopify.productType,
    price: shopify.defaultPrice,
    compareAtPrice: compareAtPrice && compareAtPrice,
    productUrl: `https://districtvision.com/products/${main.slug.current}`,
    imageUrl: main.mainImage?.asset?.url,
  };

  useEffect(() => {
    setPage(slug.current);
    setIsMounted(true);

    // Fire analytics event
    // @ts-ignore
    window.analytics && window.analytics.productView(productAnalyticsObject);
  }, []);

  const url = `products/${slug.current}`;

  return (
    <RevealOnMount location={location}>
      <div
        dangerouslySetInnerHTML={{
          __html: `
            <script type="application/ld+json">
              ${JSON.stringify(ProductSchema(main, shopify))}
            </script>
          `,
        }}
      />
      <SEO metaInfo={meta} slug={slug.current} pagePath={url} />
      <div className={cx(pageContext.main.slug.current)}>
        <ProductHero
          main={main}
          product={shopify}
          productOptions={productOptions}
          liveVariants={liveVariants}
          activeLiveVariant={activeLiveVariant}
          activeVariantId={activeVariantId}
          onVariantSelect={handleVariantSelect}
          compareAtPrice={compareAtPrice}
        />
        <ProductForm
          className="hide-min-large"
          {...shopify}
          activeLiveVariant={activeLiveVariant}
          activeVariantId={activeVariantId}
          available={available}
          productTrackingObject={productAnalyticsObject}
          isPreOrder={isPreOrder}
        />
        {!productCourse &&
          modules &&
          modules.map((module) => {
            return <ProductContentModule key={module._key} section={module} />;
          })}
        <ProductForm
          className="hide-max-medium"
          {...shopify}
          activeLiveVariant={activeLiveVariant}
          activeVariantId={activeVariantId}
          available={available}
          productTrackingObject={productAnalyticsObject}
          isPreOrder={isPreOrder}
        />
      </div>
    </RevealOnMount>
  );
};

export default Product;
